import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Users, Mail, Phone, Building2, 
  Calendar, MessageSquare, RefreshCw, Download,
  Lock, Eye, EyeOff, AlertCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = ({ onBack }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  const [demoRequests, setDemoRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if already authenticated in session
  useEffect(() => {
    const auth = sessionStorage.getItem('skydoc-admin-auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    
    try {
      await axios.post(`${API}/admin/verify`, { password });
      sessionStorage.setItem('skydoc-admin-auth', 'true');
      setIsAuthenticated(true);
    } catch (err) {
      setLoginError('Invalid password. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('skydoc-admin-auth');
    setIsAuthenticated(false);
    setPassword('');
  };

  const fetchDemoRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/demo-requests`);
      setDemoRequests(response.data);
    } catch (err) {
      setError('Failed to load demo requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchDemoRequests();
    }
  }, [isAuthenticated]);

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Company', 'Role', 'Phone', 'Message', 'Date'];
    const rows = demoRequests.map(req => [
      req.name,
      req.email,
      req.company,
      req.role || '',
      req.phone || '',
      req.message || '',
      new Date(req.created_at).toLocaleString()
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `demo-requests-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const sendEmailNotification = (req) => {
    const subject = encodeURIComponent(`New Demo Request from ${req.name} - ${req.company}`);
    const body = encodeURIComponent(
      `New Demo Request\n\n` +
      `Name: ${req.name}\n` +
      `Email: ${req.email}\n` +
      `Company: ${req.company}\n` +
      `Role: ${req.role || 'Not specified'}\n` +
      `Phone: ${req.phone || 'Not provided'}\n` +
      `Message: ${req.message || 'No message'}\n\n` +
      `Submitted: ${new Date(req.created_at).toLocaleString()}`
    );
    
    // Open email client with all 3 recipients
    window.location.href = `mailto:anusha.bhupati@exoralabs.com,nandini.k@exoralabs.com,drrohithg@gmail.com?subject=${subject}&body=${body}`;
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 force-light-text">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Access</h1>
            <p className="text-slate-600 mt-2">Enter password to view demo requests</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none pr-12"
                  placeholder="Enter admin password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            {loginError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-4 h-4" />
                {loginError}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loginLoading ? 'Verifying...' : 'Login'}
            </button>
          </form>
          
          <button
            onClick={onBack}
            className="w-full mt-4 py-3 text-slate-600 hover:text-slate-800 transition-colors text-sm"
          >
            ← Back to Website
          </button>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8 force-light-text">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack}
              className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Demo Requests</h1>
              <p className="text-slate-600">Manage incoming demo requests</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={fetchDemoRequests}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={exportToCSV}
              disabled={demoRequests.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
            >
              <Lock className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{demoRequests.length}</p>
                <p className="text-sm text-slate-600">Total Requests</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {demoRequests.filter(r => {
                    const date = new Date(r.created_at);
                    const today = new Date();
                    return date.toDateString() === today.toDateString();
                  }).length}
                </p>
                <p className="text-sm text-slate-600">Today</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {new Set(demoRequests.map(r => r.company)).size}
                </p>
                <p className="text-sm text-slate-600">Unique Companies</p>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Recipients Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Notification Recipients:</strong> Click "Notify Team" on any request to email all contacts:
            <span className="ml-2">Anusha, Nandini (India) & Rohith (UK)</span>
          </p>
        </div>

        {/* Requests List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Loading demo requests...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
            <button onClick={fetchDemoRequests} className="mt-4 text-blue-600 hover:underline">
              Try again
            </button>
          </div>
        ) : demoRequests.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No demo requests yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {demoRequests.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((req, index) => (
              <div 
                key={req.id || index}
                className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                        <span className="text-lg font-semibold text-slate-600">
                          {req.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{req.name}</h3>
                        <p className="text-sm text-slate-600">{req.role || 'Role not specified'}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${req.email}`} className="text-blue-600 hover:underline">
                          {req.email}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Building2 className="w-4 h-4" />
                        {req.company}
                      </div>
                      {req.phone && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="w-4 h-4" />
                          <a href={`tel:${req.phone}`} className="text-blue-600 hover:underline">
                            {req.phone}
                          </a>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-slate-600">
                        <Calendar className="w-4 h-4" />
                        {new Date(req.created_at).toLocaleString()}
                      </div>
                    </div>
                    
                    {req.message && (
                      <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-start gap-2">
                          <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5" />
                          <p className="text-sm text-slate-700">{req.message}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => sendEmailNotification(req)}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                    >
                      Notify Team
                    </button>
                    <a
                      href={`mailto:${req.email}`}
                      className="px-4 py-2 bg-slate-100 text-slate-700 text-sm rounded-lg hover:bg-slate-200 transition-colors text-center"
                    >
                      Reply
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
